/**
 * The last word when a screen fails to draw (9 Oct 2026).
 *
 * With no boundary anywhere, one render fault — a record shaped differently
 * than a screen expected, a route chunk that could not be fetched twice in a
 * row during an update — unmounted the whole app and left a white page with no
 * way back but the browser's own reload, which people on a phone do not know
 * to look for. This catches it and offers the reload.
 *
 * Two kinds of fault, told apart (9 Oct 2026, the owner's phone showed this
 * card during an update and Reload brought it straight back):
 *
 *   - The app's own code could not be fetched: the server is swapping in a new
 *     version. Nothing is wrong with the screen, so the card says so and loads
 *     the new version by itself, every few seconds, until it is there.
 *   - Anything else: a real fault. The card names it in small print, so a
 *     screenshot says what broke.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw } from 'lucide-react';

import { clearChunkReloadMark } from '@/lib/lazy-retry';
import { Button } from './ui';

interface State {
  error: Error | null;
}

/** How long the card waits before loading the new version by itself. */
const UPDATE_RELOAD_MS = 6_000;

/** The browser could not fetch a piece of the app — Chrome, Safari and Firefox each word it differently. */
function isMissingCode(error: Error): boolean {
  return /dynamically imported module|Importing a module script failed|error loading dynamically imported|Loading chunk|ChunkLoadError|Unable to preload CSS/i.test(
    `${error.name} ${error.message}`,
  );
}

function reload(): void {
  // A fresh start: the reload-once guard must not refuse the next try.
  clearChunkReloadMark();
  window.location.reload();
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };
  private timer: number | undefined;

  static getDerivedStateFromError(error: unknown): State {
    return { error: error instanceof Error ? error : new Error(String(error)) };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('The app failed to draw', error, info.componentStack);
    if (isMissingCode(error)) this.timer = window.setTimeout(reload, UPDATE_RELOAD_MS);
  }

  override componentWillUnmount(): void {
    window.clearTimeout(this.timer);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    const updating = isMissingCode(error);
    return (
      <div className="flex min-h-dvh items-center justify-center bg-paper px-4">
        <div role="alert" className="flex max-w-sm flex-col items-center gap-3 text-center">
          <span
            aria-hidden
            className={
              updating
                ? 'flex h-12 w-12 items-center justify-center rounded-full bg-accent-soft text-accent [&>svg]:h-5 [&>svg]:w-5'
                : 'flex h-12 w-12 items-center justify-center rounded-full bg-[color-mix(in_oklab,var(--c-danger)_12%,transparent)] text-danger [&>svg]:h-5 [&>svg]:w-5'
            }
          >
            {updating ? <RotateCcw /> : <AlertTriangle />}
          </span>
          <div>
            <p className="font-display text-body font-semibold text-ink">
              {updating ? 'The app is being updated' : 'This screen could not be shown'}
            </p>
            <p className="mt-1 text-caption leading-relaxed text-muted">
              {updating
                ? 'It opens again by itself in a few seconds — nothing you saved is lost.'
                : 'Reload to bring it back — nothing you saved is lost. If this comes back, send a screenshot of this screen.'}
            </p>
          </div>
          <Button variant="primary" size="sm" onClick={reload} leftIcon={<RotateCcw className="h-3.5 w-3.5" aria-hidden />}>
            Reload
          </Button>
          {updating ? null : (
            <p className="max-w-full break-words font-data text-micro text-faint">
              {window.location.pathname} · {error.name}: {error.message.slice(0, 160)}
            </p>
          )}
        </div>
      </div>
    );
  }
}
